import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { GridPricePackagePeriodCell } from '../../components/GridPricePackagePeriodCell/GridPricePackagePeriodCell';
import { GridPricePackageStatusCell } from '../../components/GridPricePackageStatusCell/GridPricePackageStatusCell';
import { GridPricePackageTargetingsCell } from '../../components/GridPricePackageTargetingsCell/GridPricePackageTargetingsCell';
import { GridRetargetingNameCell } from '../../components/GridRetargetingNameCell/GridRetargetingNameCell';
import { MoneyCell } from '../../components/MoneyCell/MoneyCell';
import { NumberCell } from '../../components/NumberCell/NumberCell';
import { PricePackagesClientContainer } from '../../containers/PricePackageClientContainer/PricePackagesClientContainer';
import { PricePackagesMenuActionsContainer } from '../../containers/PricePackagesMenuActionsContainer/PricePackagesMenuActionsContainer';
import { addSettingsToDecl, arrangeInOrder } from '../../service/DeclarationHelper';
import { isStatusTextVisible } from '../../service/GridHelpers/GridHelpers';
import { ColumnsSettings, ColumnsSettingsViewDict, pricePackages } from '../../typings/common';
import { GridCellProps } from '../../typings/grid';
import { PricePackageBaseFields, PricePackageRow, PricePackagesMenuActions } from '../../typings/pricePackages';

import * as keyset from './pricePackages.i18n';

const i18n = i18nFactory(keyset);

export enum PricePackagesColumnKey {
    settings = 'settings',
    name = 'name',
    status = 'status',
    period = 'period',
    showsMin = 'showsMin',
    showsMax = 'showsMax',
    priceContext = 'priceContext',
    targetings = 'targetings',
    clients = 'clients'
}

interface PricePackagesColumnDeclBase {
    key: PricePackagesColumnKey;
    title: string;
    hintInfo?: string;
    isSortable?: boolean;
    maxWidth?: number;
    fixed?: boolean;
    minWidth?: number;
    cellClass?: string;
    mandatory?: boolean;
    align?: 'right' | 'center' | 'left';
    CellComponent: React.MemoExoticComponent<(props: GridCellProps<PricePackageRow>) => JSX.Element | null>;
}

export interface PricePackagesColumnDecl extends PricePackagesColumnDeclBase {
    width: number;
    hidden?: boolean;
}

export const PricePackagesColumnOrder = [
    PricePackagesColumnKey.settings,
    PricePackagesColumnKey.name,
    PricePackagesColumnKey.status,
    PricePackagesColumnKey.period,
    PricePackagesColumnKey.showsMin,
    PricePackagesColumnKey.showsMax,
    PricePackagesColumnKey.priceContext,
    PricePackagesColumnKey.targetings,
    PricePackagesColumnKey.clients
];

export function getDefaultDynamicPricePackagesColumnsFields(): ColumnsSettings {
    return {
        columnOrder: PricePackagesColumnOrder,
        columnViewInfo: {
            [PricePackagesColumnKey.settings]: {
                width: 40,
                widthCompact: 40,
                hidden: false
            },
            [PricePackagesColumnKey.name]: {
                width: 300,
                widthCompact: 300,
                hidden: false
            },
            [PricePackagesColumnKey.status]: {
                width: 150,
                widthCompact: 150,
                hidden: false
            },
            [PricePackagesColumnKey.period]: {
                width: 200,
                widthCompact: 200,
                hidden: false
            },
            [PricePackagesColumnKey.showsMin]: {
                width: 150,
                widthCompact: 150,
                hidden: false
            },
            [PricePackagesColumnKey.showsMax]: {
                width: 150,
                widthCompact: 150,
                hidden: false
            },
            [PricePackagesColumnKey.priceContext]: {
                width: 100,
                widthCompact: 100,
                hidden: false
            },
            [PricePackagesColumnKey.targetings]: {
                width: 200,
                widthCompact: 200,
                hidden: false
            },
            [PricePackagesColumnKey.clients]: {
                width: 100,
                widthCompact: 100,
                hidden: false
            }
        }
    };
}

const getFixedPricePackagesColumns = (): Array<PricePackagesColumnDeclBase> => {
    return [
        {
            title: '',
            key: PricePackagesColumnKey.settings,
            cellClass: 'settings-cell',
            isSortable: false,
            mandatory: true,
            fixed: true,
            minWidth: 40,
            maxWidth: 40,
            CellComponent: React.memo(
                ({
                    rowIndex,
                    actions
                }: GridCellProps<PricePackageBaseFields, PricePackagesMenuActions>): JSX.Element => (
                    <PricePackagesMenuActionsContainer index={rowIndex} actions={actions} />
                )
            )
        },
        {
            title: i18n('name'),
            key: PricePackagesColumnKey.name,
            isSortable: false,
            fixed: true,
            minWidth: 150,
            maxWidth: 600,
            CellComponent: React.memo(
                ({ rowData: { id, title } }: GridCellProps<PricePackageRow>): JSX.Element => (
                    <GridRetargetingNameCell id={id} name={title} />
                )
            )
        }
    ];
};

const getPricePackagesColumnsBase = (columnsSettings: ColumnsSettings): Array<PricePackagesColumnDeclBase> => {
    return [
        {
            title: i18n('status'),
            key: PricePackagesColumnKey.status,
            isSortable: false,
            minWidth: 35,
            maxWidth: 150,
            CellComponent: React.memo(
                ({ rowData: { statusApprove } }: GridCellProps<PricePackageRow>): JSX.Element => {
                    const columnViewInfo = columnsSettings.columnViewInfo[PricePackagesColumnKey.status];

                    return (
                        <GridPricePackageStatusCell
                            status={statusApprove}
                            isTextVisible={isStatusTextVisible(true, columnViewInfo)}
                        />
                    );
                }
            )
        },
        {
            title: i18n('period'),
            key: PricePackagesColumnKey.period,
            isSortable: false,
            minWidth: 200,
            maxWidth: 250,
            align: 'left',
            CellComponent: React.memo(
                ({ rowData: { dateStart, dateEnd } }: GridCellProps<PricePackageRow>): JSX.Element => {
                    return dateStart && dateEnd ? (
                        <GridPricePackagePeriodCell start={dateStart} finish={dateEnd} />
                    ) : (
                        <></>
                    );
                }
            )
        },
        {
            title: i18n('shows-min'),
            key: PricePackagesColumnKey.showsMin,
            isSortable: false,
            align: 'right',
            minWidth: 150,
            maxWidth: 250,
            CellComponent: React.memo(
                ({ rowData: { orderVolumeMin } }: GridCellProps<PricePackageRow>): JSX.Element => {
                    return <NumberCell num={Number(orderVolumeMin)} />;
                }
            )
        },
        {
            title: i18n('shows-max'),
            key: PricePackagesColumnKey.showsMax,
            isSortable: false,
            align: 'right',
            minWidth: 150,
            maxWidth: 250,
            CellComponent: React.memo(
                ({ rowData: { orderVolumeMax } }: GridCellProps<PricePackageRow>): JSX.Element => {
                    return <NumberCell num={Number(orderVolumeMax)} />;
                }
            )
        },
        {
            title: i18n('price-context'),
            key: PricePackagesColumnKey.priceContext,
            isSortable: false,
            align: 'right',
            minWidth: 100,
            maxWidth: 200,
            CellComponent: React.memo(
                ({ rowData: { price, currency } }: GridCellProps<PricePackageRow>): JSX.Element => {
                    return <MoneyCell num={+price} fractionalPartLength={2} currency={currency} />;
                }
            )
        },
        {
            title: i18n('targetings'),
            key: PricePackagesColumnKey.targetings,
            isSortable: false,
            minWidth: 200,
            maxWidth: 300,
            CellComponent: React.memo(
                ({ rowData: { targetingsFixed, targetingsCustom } }: GridCellProps<PricePackageRow>): JSX.Element => {
                    return (
                        <GridPricePackageTargetingsCell
                            targetingsFixed={targetingsFixed}
                            targetingsCustom={targetingsCustom}
                        />
                    );
                }
            )
        },
        {
            title: i18n('clients'),
            key: PricePackagesColumnKey.clients,
            isSortable: false,
            align: 'right',
            minWidth: 100,
            maxWidth: 200,
            CellComponent: React.memo(
                ({
                    rowData: { id, clientIds, statusApprove },
                    rowIndex
                }: GridCellProps<PricePackageRow>): JSX.Element => {
                    return (
                        <PricePackagesClientContainer
                            key={id}
                            id={rowIndex}
                            status={statusApprove}
                            clientIds={clientIds}
                        />
                    );
                }
            )
        }
    ];
};

export function getPricePackagesColumns(columnsSettings: ColumnsSettingsViewDict): Array<PricePackagesColumnDecl> {
    const fixedColumns = getFixedPricePackagesColumns().map(column => {
        return addSettingsToDecl(column, columnsSettings, pricePackages);
    });

    const result = getPricePackagesColumnsBase(columnsSettings[pricePackages]).map(column => {
        return addSettingsToDecl(column, columnsSettings, pricePackages);
    });

    return fixedColumns.concat(arrangeInOrder(result, columnsSettings[pricePackages].columnOrder));
}
